
import React, { useState, useEffect } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { discipleshipService } from '../../services/discipleship.service';
import type { DiscipleshipClass, DiscipleshipClassFormData } from '../../types/discipleship';
import { FaGraduationCap, FaUserTie, FaCalendarAlt, FaClock, FaSave, FaEdit, FaCheck, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { usePermissions } from '../../hooks/usePermissions';

const DEFAULT_PROGRAMS = ["새가족반", "성장반", "제자반", "사역반"];

interface ClassFormProps {
  editingClass?: DiscipleshipClass | null;
  onSuccess: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  error?: string | null;
  readOnly?: boolean;
}

export const ClassForm: React.FC<ClassFormProps> = ({
  editingClass,
  onSuccess,
  onCancel,
  isLoading = false,
  error,
  readOnly = false
}) => {
  const { canWrite } = usePermissions();
  const isReadOnly = readOnly || !canWrite('discipleship');
  const { register, handleSubmit, watch, formState: { errors, isSubmitting, isValid }, reset } = useForm<DiscipleshipClassFormData>({
    mode: 'onChange',
    defaultValues: {
      programName: DEFAULT_PROGRAMS[0],
      description: '',
      durationInWeeks: 4,
      startDate: ''
    }
  });
  
  const programNameValue = watch("programName");
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (editingClass) {
      const isDefaultProgram = DEFAULT_PROGRAMS.includes(editingClass.programName);
      reset({
        ...editingClass,
        programName: isDefaultProgram ? editingClass.programName : 'other',
        programNameOther: isDefaultProgram ? '' : editingClass.programName,
      });
    } else {
      reset({
        programName: DEFAULT_PROGRAMS[0],
        description: '',
        durationInWeeks: 4,
        startDate: new Date().toISOString().split('T')[0]
      });
    }
  }, [editingClass, reset]);

  const onSubmit: SubmitHandler<DiscipleshipClassFormData> = async (data) => {
    setIsSubmittingForm(true);
    setSubmitSuccess(false);
    
    try {
      if (editingClass) {
        await discipleshipService.updateClass(editingClass.id, data);
        setSubmitSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 1000);
      } else {
        await discipleshipService.addClass(data);
        setSubmitSuccess(true);
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error("과정 처리 중 오류:", error);
      setSubmitSuccess(false);
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // 폼 필드 컴포넌트
  const FormField = ({
    label,
    children,
    required = false,
    description
  }: {
    label: string;
    children: React.ReactNode;
    required?: boolean;
    description?: string;
  }) => (
    <div className="space-y-2">
      <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {children}
      {description && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      )}
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <FaGraduationCap className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {editingClass ? '양육 과정 수정' : '새 양육 과정 개설'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {editingClass ? '기존 과정 정보를 수정합니다' : '새로운 양육 과정을 개설합니다'}
            </p>
          </div>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            취소
          </button>
        )}
      </div>

      {/* 읽기 전용 모드 알림 */}
      {isReadOnly && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaLock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              읽기 전용 모드입니다. 양육 과정 정보를 수정할 권한이 없습니다.
            </span>
          </div>
        </div>
      )}
      
      {/* 오류 메시지 */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaExclamationTriangle className="h-5 w-5 text-red-500" />
            <span className="text-red-700 dark:text-red-300 font-medium">오류 발생</span>
          </div>
          <p className="text-red-600 dark:text-red-400 mt-1">{error}</p>
        </div>
      )}

      {/* 성공 메시지 */}
      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaCheck className="h-5 w-5 text-green-500" />
            <span className="text-green-700 dark:text-green-300 font-medium">성공</span>
          </div>
          <p className="text-green-600 dark:text-green-400 mt-1">
            {editingClass ? '과정이 성공적으로 수정되었습니다' : '새로운 과정이 개설되었습니다'}
          </p>
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span className="text-blue-700 dark:text-blue-300">로딩 중...</span>
          </div>
        </div>
      )}

      <form onSubmit={isReadOnly ? undefined : handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 과정 이름 */}
          <FormField
            label="과정 이름"
            required={true}
            description="기존 과정을 선택하거나 새로운 과정을 직접 입력하세요"
          >
            <select
              id="programName"
              {...register("programName", { required: "과정 이름을 선택해주세요" })}
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            >
              {DEFAULT_PROGRAMS.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
              <option value="other">신규 과정 직접 입력</option>
            </select>
            {errors.programName && (
              <p className="text-sm text-red-500 mt-1" role="alert">{errors.programName.message as string}</p>
            )}
          </FormField>

          {/* 신규 과정 이름 (직접 입력 시) */}
          {programNameValue === 'other' && (
            <FormField
              label="새 과정 이름"
              required={true}
            >
              <input
                id="programNameOther"
                {...register("programNameOther", { required: "새 과정 이름을 입력해주세요." })}
                placeholder="새로운 양육 과정 이름을 입력하세요"
                lang="ko"
                inputMode="text"
                disabled={isReadOnly}
                className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              />
              {errors.programNameOther?.message && (
                <p className="text-sm text-red-500 mt-1" role="alert">{errors.programNameOther.message as string}</p>
              )}
            </FormField>
          )}

          {/* 기수/세션 */}
          <FormField
            label="기수/세션"
            required={true}
            description="예: 2025년 1차, 3기, 가을학기 등"
          >
            <input
              id="session"
              {...register("session", {
                required: "기수는 필수 항목입니다.",
                pattern: {
                  value: /^[0-9가-힣년차기학기]+$/,
                  message: "올바른 형식으로 입력해주세요 (예: 2025년 1차)"
                }
              })}
              placeholder="예: 2025년 1차"
              lang="ko"
              inputMode="text"
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
            {errors.session?.message && (
              <p className="text-sm text-red-500 mt-1" role="alert">{errors.session.message as string}</p>
            )}
          </FormField>

          {/* 담당자 */}
          <FormField
            label="담당자"
            required={true}
            description="양육 과정을 담당하는 교사/목사 이름"
          >
            <input
              id="teacher"
              {...register("teacher", {
                required: "담당자는 필수 항목입니다.",
                minLength: {
                  value: 2,
                  message: "담당자 이름은 2자 이상이어야 합니다."
                }
              })}
              placeholder="담당자 이름을 입력하세요"
              lang="ko"
              inputMode="text"
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
            {errors.teacher?.message && (
              <p className="text-sm text-red-500 mt-1" role="alert">{errors.teacher.message as string}</p>
            )}
          </FormField>

          {/* 과정 기간 */}
          <FormField
            label="과정 기간"
            required={true}
            description="주 단위로 입력 (최소 1주)"
          >
            <input
              id="durationInWeeks"
              type="number"
              {...register("durationInWeeks", {
                required: "과정 기간은 필수 항목입니다.",
                valueAsNumber: true,
                min: {
                  value: 1,
                  message: "과정 기간은 1주 이상이어야 합니다."
                },
                max: {
                  value: 52,
                  message: "과정 기간은 52주를 초과할 수 없습니다."
                }
              })}
              placeholder="주 수를 입력하세요"
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
            {errors.durationInWeeks?.message && (
              <p className="text-sm text-red-500 mt-1" role="alert">{errors.durationInWeeks.message as string}</p>
            )}
          </FormField>

          {/* 시작일 */}
          <FormField
            label="시작일"
            required={false}
            description="선택 사항입니다. 입력하지 않으면 자동으로 오늘로 설정됩니다"
          >
            <input
              id="startDate"
              type="date"
              {...register("startDate")}
              disabled={isReadOnly}
              className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
          </FormField>
        </div>
        
        {/* 과정 설명 */}
        <FormField
          label="과정 설명"
          required={false}
          description="양육 과정의 상세 설명을 입력하세요"
        >
          <textarea
            id="description"
            {...register("description")}
            placeholder="과정의 목표, 내용, 특징 등을 상세히 설명해주세요"
            rows={4}
            lang="ko"
            inputMode="text"
            disabled={isReadOnly}
            className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm resize-none ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
          />
        </FormField>

        {/* 버튼 영역 */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-all duration-200"
            >
              취소
            </button>
          )}
          {!isReadOnly && (
            <button
              type="submit"
              disabled={isSubmitting || isSubmittingForm || !isValid}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
            >
              {(isSubmitting || isSubmittingForm) ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>처리 중...</span>
                </>
              ) : (
                <>
                  <FaSave className="h-4 w-4" />
                  <span>{editingClass ? '수정하기' : '개설하기'}</span>
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ClassForm;
